<template>
  <q-checkbox
    :model-value="effectiveModelValue"
    class="checkbox-standard"
    :class="classes"
    :name="name"
    :toggle-order="toggleOrder"
    :toggle-indeterminate="toggleIndeterminate"
    :keep-color="keepColor"
    :tabindex="tabindex"
    :checked-icon="checkedIcon"
    :unchecked-icon="uncheckedIcon"
    :indeterminate-icon="indeterminateIcon"
    :label="label"
    :left-label=" leftLabel"
    :val="val"
    :true-value="trueValue"
    :false-value="falseValue"
    :indeterminate-value="indeterminateValue"
    :size="size"
    :disable="isDisabled"
    :dense="isDense"
    @update:model-value="v => emit('update:modelValue', v)"
  >
    <template
      v-for="(slot, slotName) in $slots"
      #[slotName]="scope"
      :key="slotName"
    >
      <div
        v-if="alert && isIcon"
        class="button-standard--alert"
        :class="alertClasses"
      />
      <slot
        :name="slotName"
        v-bind="scope || {}"
      />
    </template>
  </q-checkbox>
</template>

<script setup>
import { computed } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Checkbox' })
// const model = defineModel({ type: [Array, Number, String, Boolean], default: false })
const emit = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: { type: [Array, Number, String, Boolean], default: null },
  val: { type: [Number, String, Boolean, Object], default: null },
  trueValue: { type: [Number, String, Boolean, Object], default: true },
  falseValue: { type: [Number, String, Boolean, Object], default: false },
  indeterminateValue: { type: [Number, String], default: null },
  indeterminate: { type: Boolean, default: false },
  name: { type: String, default: null },
  toggleOrder: { type: String, default: 'tf', validator: v => ['tf', 'ft'].includes(v) },
  toggleIndeterminate: { type: Boolean, default: false },
  keepColor: { type: Boolean, default: false },
  tabindex: { type: [Number, String], default: null },
  checkedIcon: { type: String, default: 'mono-check' },
  uncheckedIcon: { type: String, default: null },
  indeterminateIcon: { type: String, default: null },
  label: { type: String, default: null },
  leftLabel: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'md',
    validate: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  color: {
    type: String,
    default: 'primary',
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert'
    ].includes(v)
  },
  isDense: { type: Boolean, default: false },
  isDisabled: { type: Boolean, default: false }
})

const effectiveModelValue = computed(() => props.indeterminate ? props.indeterminateValue : props.modelValue)

const classes = computed(() => cx(
  `size-${props.size}`,
  `color-${props.color}`,
  `icon-color-${props.color}`,
  props.leftLabel && 'is-left-label',
  props.isDense && 'is-dense',
  props.isDisabled && 'is-disabled'
))
</script>

<style scoped>
@reference "#app-styles.css";

.checkbox-standard {
  /* @apply flex-1; */
  @apply w-auto h-auto;

  &:deep(.q-checkbox__bg) {
    @apply layer-base border edge-base rounded-sm;
    @apply top-0 left-0 w-full h-full;
  }
  &:deep(.q-checkbox__truthy) { @apply stroke-none }
  &:deep(.q-checkbox__indet) {}

  &:deep(.q-checkbox__inner--truthy) { @apply rounded-sm; }
  &:deep(.q-checkbox__inner--truthy),
  &:deep(.q-checkbox__inner--indet) {
    .q-checkbox__bg { @apply bg-current border border-current; }
  }
  &:deep(.q-checkbox__inner--indet) {
    .q-checkbox__bg { @apply bg-current/50; }
  }
  &:deep(.q-checkbox__inner--truthy) {
    @apply bg-current;
    path {
      stroke-dashoffset: 0;
      transition: stroke-dashoffset 0.18s cubic-bezier(0.4, 0, 0.6, 1) 0ms;
    }
    rect {
      transition: fill 0.18s cubic-bezier(0.4, 0, 0.6, 1) 0ms;
    }
  }
  &:deep(.q-checkbox__icon) { @apply text-white; }

  &.size-xs {
    @apply gap-0.5;
    &:deep(.q-checkbox__inner) { @apply text-[10px] leading-3 w-3 min-w-3 h-3 rounded-sm; }
    &:deep(.q-checkbox__icon) { @apply text-[10px] leading-2.5; }
  }
  &.size-sm {
    @apply gap-0.75;
    &:deep(.q-checkbox__inner) { @apply text-[12px] leading-3.5 w-3.5 min-w-3.5 h-3.5; }
    &:deep(.q-checkbox__icon) { @apply text-[12px] leading-3; }
  }
  &.size-md {
    @apply gap-1;
    &:deep(.q-checkbox__inner) { @apply text-[14px] leading-4 w-4 min-w-4 h-4; }
    &:deep(.q-checkbox__icon) { @apply text-[14px] leading-3.5; }
  }
  &.size-lg {
    @apply gap-1.25;
    &:deep(.q-checkbox__inner) { @apply text-[14px] leading-4 w-4.5 min-w-4.5 h-4.5; }
    &:deep(.q-checkbox__icon) { @apply text-[16px] leading-4; }
  }
  &.size-xl {
    @apply gap-1.25;
    &:deep(.q-checkbox__inner) { @apply text-[14px] leading-4 w-5 min-w-5 h-5; }
    &:deep(.q-checkbox__icon) { @apply text-[18px] leading-4.5; }
  }

  &.color-primary :deep(.q-checkbox__inner) { @apply text-primary-mid; }
  &.color-secondary :deep(.q-checkbox__inner) { @apply text-secondary-mid; }
  &.color-positive :deep(.q-checkbox__inner) { @apply text-positive-mid; }
  &.color-negative :deep(.q-checkbox__inner) { @apply text-negative-mid; }
  &.color-warning :deep(.q-checkbox__inner) { @apply text-warning-mid; }
  &.color-info :deep(.q-checkbox__inner) { @apply text-info-mid; }
  &.color-accent :deep(.q-checkbox__inner) { @apply text-accent-mid; }
  &.color-invert :deep(.q-checkbox__inner) { @apply text-info-mid; }
  &.color-white :deep(.q-checkbox__inner) { @apply text-info-mid; }
  &.color-black :deep(.q-checkbox__inner) { @apply text-info-mid; }
}
</style>
